<script setup lang="ts">
import { computed, ref, watch, onMounted } from "vue";
import i18n from "@/plugins/i18n";

const currentSelectedImprovements = ref<Array<string>>([]);
const currentComment = ref<string>("");
const currentRating = ref<number>(0);

const props = withDefaults(
  defineProps<{
    mainLabel: string;
    includeFeedbackText?: boolean;
    improvementOptions?: Array<{
      value: string;
      text: string;
    }>;
    name: string;
    rating?: number;
    selectedImprovements?: Array<string>;
    comment?: string;
  }>(),
  {
    includeFeedbackText: true,
    rating: 0,
  }
);

watch(
  () => props.rating,
  (newValue) => {
    currentRating.value = newValue;
  },
  { immediate: true }
);

watch(
  () => props.selectedImprovements,
  (newValue) => {
    currentSelectedImprovements.value = newValue;
  },
  { immediate: true }
);

watch(
  () => props.comment,
  (newValue) => {
    currentComment.value = newValue;
  },
  { immediate: true }
);

const emit = defineEmits(["update"]);

const emitUpdate = () => {
  emit("update", {
    rating: currentRating.value,
    checkboxes: currentSelectedImprovements.value,
    feedback: currentComment.value,
  });
};

const handleSelectionChange = (value: string) => {
  if (currentSelectedImprovements.value.includes(value)) {
    currentSelectedImprovements.value = currentSelectedImprovements.value.filter(
      (item) => item !== value
    );
  } else {
    currentSelectedImprovements.value.push(value);
  }

  emitUpdate();
};

const handleCommentChange = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  currentComment.value = target.value;
  emitUpdate();
};

const handleStarClick = (event: Event) => {
  const target = event.currentTarget as HTMLSelectElement;
  const starIndex = Number(target.getAttribute("data-index"));
  currentRating.value = starIndex;
  emitUpdate();
};

const ratingText = computed((): string => {
  if (currentRating.value < 1) {
    return "";
  }
  return i18n.t(`reviewsV2.RATING_${currentRating.value}`).toString();
});
</script>

<template>
  <div class="border-1 border-solid border-border-gray padding-a-8">
    <fieldset>
      <label
        class="font-bold rating-label font-16 margin-b-4 display-block bold"
        >{{ mainLabel }}</label
      >
      <ul>
        <li v-for="idx in 5" class="display-inline-block" :key="idx">
          <CUIcon
            width="40"
            height="40"
            view-box="0 0 40 40"
            :data-index="idx"
            @click="handleStarClick"
            :color="currentRating >= idx ? 'yellow' : 'gray-light'"
            >star</CUIcon
          >
        </li>
      </ul>
      <span
        class="rating-text margin-t-4 font-12 display-block"
        v-html="ratingText"
      ></span>
    </fieldset>

    <fieldset
      class="text-left margin-t-6 margin-b-6"
      v-if="props.improvementOptions && currentRating < 5 && currentRating > 0"
    >
      <legend class="display-block margin-b-2 font-16">
        {{ $t("reviewsV2.IMPROVEMENT_CHECKBOXES_LABEL") }}
        <span class="font-12 gray-light"
          >({{ $t("reviewsV2.OPTIONAL_INPUT_LABEL") }})</span
        >
      </legend>
      <v-chip-group column multiple>
        <v-chip
          v-for="(item, idx) in props.improvementOptions"
          :key="idx"
          :class="{ 'primary--text': currentSelectedImprovements.includes(item.value) }"
          @click="handleSelectionChange(item.value)"
        >
          {{ item.text }}
        </v-chip>
      </v-chip-group>
    </fieldset>

    <fieldset
      v-if="includeFeedbackText && currentRating < 5 && currentRating > 0"
      class="text-left margin-t-6 margin-b-6"
    >
      <label class="bold display-block margin-b-2 font-16">
        {{ $t("reviewsV2.FEEDBACK_INPUT_LABEL") }}
        <span class="font-12 gray-light"
          >({{ $t("reviewsV2.OPTIONAL_INPUT_LABEL") }})</span
        >
      </label>
      <textarea
        :value="currentComment"
        @change="handleCommentChange"
      ></textarea>
    </fieldset>
  </div>
</template>

<style scoped lang="scss">
fieldset {
  border: 0;
}

textarea {
  border: 1px solid $gray-mid-light;
  background-color: $gray-lighter;
  padding: 8px;
  width: 100%;
  height: 100px;
  font-size: 0.8rem;
}
</style>
